/**
 * ImageLibrary Component
 *
 * Displays template images in a grid with preview, copy variable, and delete actions
 */

import {
  Stack,
  Text,
  Paper,
  Group,
  ActionIcon,
  Tooltip,
  Image,
  Badge,
  SimpleGrid,
  Card,
  Box,
  CopyButton,
  Alert
} from "@mantine/core";
import { IconTrash, IconCopy, IconCheck, IconLink, IconCloudUpload, IconPhotoOff } from "@tabler/icons-react";
import { useCallback } from "react";
import type { TemplateImage } from "../../types/template";

export interface ImageLibraryProperties {
  /** List of images */
  images: TemplateImage[];
  /** Callback when image is removed */
  onRemove: (imageId: string) => Promise<unknown>;
  /** Whether removal is in progress */
  isRemoving?: boolean;
  /** Callback when variable is copied (optional) */
  onVariableCopy?: (variable: string) => void;
}

/**
 * Generate the template variable for an image
 * @param imageName
 */
function getImageVariable(imageName: string): string {
  return `{{imagem.${imageName}}}`;
}

/**
 * ImageLibrary - Grid display of template images
 * @param root0
 * @param root0.images
 * @param root0.onRemove
 * @param root0.isRemoving
 * @param root0.onVariableCopy
 */
export function ImageLibrary({ images, onRemove, isRemoving = false, onVariableCopy }: ImageLibraryProperties) {
  const handleRemove = useCallback(
    async (imageId: string) => {
      if (isRemoving) return;
      await onRemove(imageId);
    },
    [onRemove, isRemoving]
  );

  if (images.length === 0) {
    return (
      <Alert icon={<IconPhotoOff size={16} />} color="gray" variant="light">
        <Text size="sm">Nenhuma imagem adicionada ainda.</Text>
        <Text size="xs" c="dimmed" mt={4}>
          Use o formulário acima para adicionar imagens via URL ou upload.
        </Text>
      </Alert>
    );
  }

  return (
    <Stack gap="md">
      <Group justify="space-between" align="center">
        <Text fw={500} size="sm">
          Imagens do Template ({images.length})
        </Text>
      </Group>

      <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="md">
        {images.map((image) => (
          <ImageCard
            key={image.id}
            image={image}
            onRemove={handleRemove}
            isRemoving={isRemoving}
            onVariableCopy={onVariableCopy}
          />
        ))}
      </SimpleGrid>
    </Stack>
  );
}

interface ImageCardProperties {
  image: TemplateImage;
  onRemove: (imageId: string) => Promise<unknown>;
  isRemoving: boolean;
  onVariableCopy?: (variable: string) => void;
}

/**
 *
 * @param root0
 * @param root0.image
 * @param root0.onRemove
 * @param root0.isRemoving
 * @param root0.onVariableCopy
 */
function ImageCard({ image, onRemove, isRemoving, onVariableCopy }: ImageCardProperties) {
  const variable = getImageVariable(image.nome);

  const handleCopy = useCallback(() => {
    if (onVariableCopy) {
      onVariableCopy(variable);
    }
  }, [variable, onVariableCopy]);

  const handleRemoveClick = useCallback(() => {
    void onRemove(image.id);
  }, [onRemove, image.id]);

  return (
    <Card shadow="sm" padding="sm" radius="md" withBorder>
      <Card.Section>
        <Box pos="relative" h={120} bg="gray.1">
          <Image
            src={image.url}
            alt={image.nome}
            h={120}
            fit="cover"
            fallbackSrc="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'%3E%3Crect fill='%23f1f3f5' width='100' height='100'/%3E%3Ctext x='50' y='50' text-anchor='middle' dominant-baseline='middle' fill='%23868e96' font-size='12'%3EErro%3C/text%3E%3C/svg%3E"
          />
          <Badge
            pos="absolute"
            top={8}
            right={8}
            size="xs"
            variant="filled"
            color={image.tipo === "storage" ? "blue" : "gray"}
            leftSection={image.tipo === "storage" ? <IconCloudUpload size={10} /> : <IconLink size={10} />}
          >
            {image.tipo === "storage" ? "Upload" : "URL"}
          </Badge>
        </Box>
      </Card.Section>

      <Stack gap="xs" mt="sm">
        <Text size="sm" fw={500} lineClamp={1}>
          {image.nome}
        </Text>

        <Paper p="xs" bg="gray.0" radius="sm">
          <Text size="xs" ff="monospace" c="dimmed" lineClamp={1}>
            {variable}
          </Text>
        </Paper>

        <Group justify="flex-end" gap="xs">
          <CopyButton value={variable}>
            {({ copied, copy }) => {
              const handleCopyClick = () => {
                copy();
                handleCopy();
              };
              return (
                <Tooltip label={copied ? "Copiado!" : "Copiar variável"}>
                  <ActionIcon variant="light" color={copied ? "teal" : "blue"} onClick={handleCopyClick} size="sm">
                    {copied ? <IconCheck size={14} /> : <IconCopy size={14} />}
                  </ActionIcon>
                </Tooltip>
              );
            }}
          </CopyButton>

          <Tooltip label="Remover imagem">
            <ActionIcon variant="light" color="red" onClick={handleRemoveClick} loading={isRemoving} size="sm">
              <IconTrash size={14} />
            </ActionIcon>
          </Tooltip>
        </Group>
      </Stack>
    </Card>
  );
}
